import { cn } from "../../lib/utils";
import {
  IconCube,
  IconSquareKanban,
  IconMagnifier,
  IconTasks,
  IconChartBar,
} from "nucleo-glass";
import { NucleoIcon } from "./NucleoIcon";
import type { ReactNode } from "react";

interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  variant?: "default" | "board" | "search" | "checklist" | "chart";
  className?: string;
}

function GlassIconWrapper({ children }: { children: ReactNode }) {
  return (
    <div className="relative w-[48px] h-[48px] flex items-center justify-center">
      {/* Glass background */}
      <div className="absolute inset-0 rounded-[var(--radius-lg)] bg-gradient-to-br from-[var(--color-accent-900)] to-[var(--color-bg-hover)] border border-[var(--color-border-stroke)] backdrop-blur-[8px]" />
      {/* Icon */}
      <div className="relative z-10 text-[var(--color-accent-300)]">
        {children}
      </div>
    </div>
  );
}

const variantIcons: Record<NonNullable<EmptyStateProps["variant"]>, () => ReactNode> = {
  default: () => (
    <GlassIconWrapper>
      <NucleoIcon icon={<IconCube size={24} />} />
    </GlassIconWrapper>
  ),
  board: () => (
    <GlassIconWrapper>
      <NucleoIcon icon={<IconSquareKanban size={24} />} />
    </GlassIconWrapper>
  ),
  search: () => (
    <GlassIconWrapper>
      <NucleoIcon icon={<IconMagnifier size={24} />} />
    </GlassIconWrapper>
  ),
  checklist: () => (
    <GlassIconWrapper>
      <NucleoIcon icon={<IconTasks size={24} />} />
    </GlassIconWrapper>
  ),
  chart: () => (
    <GlassIconWrapper>
      <NucleoIcon icon={<IconChartBar size={24} />} />
    </GlassIconWrapper>
  ),
};

export function EmptyState({ title, description, icon, variant = "default", className }: EmptyStateProps) {
  const VariantIcon = variantIcons[variant];
  return (
    <div className={cn("flex flex-col items-center justify-center py-[var(--spacing-12)] text-center", className)}>
      <div className="mb-[var(--spacing-4)]">
        {icon ?? <VariantIcon />}
      </div>
      <h3 className="text-[14px] font-semibold text-[var(--color-text-primary)] mb-[var(--spacing-1)]">{title}</h3>
      {description && (
        <p className="text-[14px] text-[var(--color-text-muted)] max-w-[320px]">{description}</p>
      )}
    </div>
  );
}
